'use client';

import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import {
  adminBrand,
  adminNavGroups,
  adminTopNav,
  ConsoleDashboardShell,
  resolveConsoleRouteTitle,
} from '@loamify/ui';
import { canManageFeatureFlags, useAuth } from '@/lib/auth-store';
import { useSession } from '@/lib/use-session';
import { AdminRouteGuard } from './admin-route-guard';

export function AdminShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user, logout } = useAuth();
  const { loggedIn, isLoading: sessionLoading } = useSession();
  const isAuthPage = pathname === '/login';
  const visibleNavGroups = useMemo(
    () =>
      adminNavGroups.map((group) => ({
        ...group,
        items: group.items.filter(
          (item) => item.href !== '/feature-flags' || canManageFeatureFlags(user),
        ),
      })),
    [user],
  );

  if (isAuthPage) {
    return <div className="min-h-screen bg-background">{children}</div>;
  }

  return (
    <ConsoleDashboardShell
      headerTitle={resolveConsoleRouteTitle(pathname, adminTopNav, visibleNavGroups)}
      sidebar={{
        brand: adminBrand,
        topItems: adminTopNav,
        groups: visibleNavGroups,
        activeHref: pathname,
        user:
          loggedIn && user
            ? {
                name: user.email?.split('@')[0] ?? 'Admin',
                role: 'Platform Administrator',
              }
            : sessionLoading
              ? { name: 'Loading…', role: 'Platform Administrator' }
              : undefined,
        onLogout: loggedIn && user ? logout : undefined,
      }}
    >
      <AdminRouteGuard>{children}</AdminRouteGuard>
    </ConsoleDashboardShell>
  );
}
